import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, MapPin, HeartHandshake, Star, ArrowUpRight } from "lucide-react";
import { CONTACT } from "../../data";
import { HelpSearch } from "../HelpSearch";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "115%" },
  show: { y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};
const MaskLine = ({ children }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span variants={lineChild} className="block">
      {children}
    </motion.span>
  </span>
);

export const HomeHero = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={heroRef} className="relative overflow-hidden bg-secondary">
      {/* backdrop */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute top-1/3 -left-40 h-[420px] w-[420px] rounded-full bg-primary/5 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse at 60% 40%, black, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse at 60% 40%, black, transparent 75%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-28 grid lg:grid-cols-[1.05fr,1fr] gap-14 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-card border border-border shadow-sm text-accent font-semibold px-4 py-2 text-sm mb-7"
          >
            <MapPin className="h-4 w-4" /> Serving {CONTACT.location}
          </motion.div>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            aria-label="Technology Made Simple"
            className="font-heading font-extrabold text-foreground text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98]"
          >
            <MaskLine>Technology</MaskLine>
            <MaskLine>
              Made <span className="text-accent">Simple</span>
            </MaskLine>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-7 text-muted-foreground text-lg md:text-xl leading-relaxed max-w-xl"
          >
            Friendly, patient tech help for your home, your family, and your
            everyday life. No confusing jargon — just gentle, one‑on‑one guidance.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={CONTACT.phoneHref}
              data-testid="hero-call-now"
              className="group inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors shadow-lg shadow-accent/25"
            >
              <Phone className="h-5 w-5" /> Call Now
              <ArrowUpRight className="h-5 w-5 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </a>
            <Link
              to="/contact"
              data-testid="hero-book-appointment"
              className="inline-flex items-center justify-center h-14 px-8 rounded-full bg-primary text-white font-bold text-lg hover:bg-slate-800 transition-colors"
            >
              Book an Appointment
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex items-center gap-3"
          >
            <div
              data-testid="hero-rating-badge"
              className="inline-flex items-center gap-2.5 rounded-full bg-card border border-border shadow-sm pl-3 pr-4 py-2"
            >
              <div className="flex text-accent">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <span className="font-heading font-extrabold text-foreground text-base leading-none">5.0</span>
              <span className="h-4 w-px bg-border" aria-hidden="true" />
              <span className="text-muted-foreground text-sm font-medium">from happy neighbors</span>
            </div>
          </motion.div>
          <p className="mt-3 text-muted-foreground text-sm font-medium">
            Trusted by families &amp; seniors all across Columbus
          </p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-8"
          >
            <p className="font-heading font-bold text-foreground text-lg mb-3">
              Not sure where to start? Just ask.
            </p>
            <HelpSearch variant="hero" />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -inset-3 bg-accent/10 rounded-[2.2rem] -rotate-2" />
          <div className="relative overflow-hidden rounded-[2rem] shadow-2xl h-[440px] lg:h-[560px]">
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src="https://images.unsplash.com/photo-1758686254007-a1aec378eca3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Senior couple happily using a smartphone together at home"
              className="w-full h-[calc(100%+120px)] object-cover"
            />
          </div>
          <motion.div
            style={{ y: badgeY }}
            className="absolute -bottom-6 -left-4 sm:left-6 bg-card rounded-2xl shadow-xl p-5 flex items-center gap-4 max-w-[260px]"
          >
            <span className="grid place-items-center h-12 w-12 rounded-xl bg-accent/10 text-accent shrink-0">
              <HeartHandshake className="h-6 w-6" />
            </span>
            <p className="text-sm font-semibold text-foreground leading-snug">
              Patient help, at your own pace
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
